'use client'

import { ButtonHTMLAttributes } from 'react'

export type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom'

export type Order = 'primary' | 'secondary' | 'tertiary' | 'contrast' | 'report-close' | 'report-next' | 'custom'

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: Size
  order?: Order
  className?: string
  loading?: boolean
}

const getClassNames = (...classes: string[]) => classes.filter(Boolean).join(' ')

function Button(props: Props) {
  const { children, className, size = 'md', order = 'primary', disabled, loading = false, ...rest } = props
  const commonStyles = '  inline-flex items-center rounded-md  focus:outline-none text-center justify-center'
  let sizeStyles = ''
  let orderStyles = ''
  let disabledStyles = ''

  switch (size) {
    case 'xs':
      sizeStyles = 'px-2.5 py-1.5 text-xs'
      break
    case 'sm':
      sizeStyles = 'px-3 py-2 text-sm leading-4'
      break
    case 'md':
      sizeStyles = 'px-4 py-2 text-sm'
      break
    case 'lg':
      sizeStyles = 'px-4 py-2 text-base'
      break
    case 'xl':
      sizeStyles = 'px-6 py-3 text-base'
      break
    case 'custom':
      sizeStyles = ''
      break
  }

  switch (order) {
    case 'primary':
      orderStyles = 'text-white border border-transparent bg-bright-blue-50 hover:bg-bright-blue-60'
      disabledStyles = 'opacity-50 cursor-not-allowed hover:bg-navy-50'
      break
    case 'secondary':
      orderStyles = 'text-gray-900 border border-gray-200 bg-gray-100 hover:bg-gray-50'
      disabledStyles = 'opacity-50 cursor-not-allowed'
      break
    case 'tertiary':
      orderStyles = 'text-white border border-transparent bg-bright-blue-50 hover:bg-bright-blue-60'
      disabledStyles = 'opacity-50 cursor-not-allowed hover:bg-bright-blue-60'
      break
    case 'contrast':
      orderStyles = 'text-white border border-transparent bg-fuschia-50 hover:bg-fuschia-60'
      disabledStyles = 'opacity-50 cursor-not-allowed hover:bg-fuschia-60'
      break
    case 'report-close':
      orderStyles = 'border-2 border-[#3D55a9] bg-white font-bold hover:bg-[#3D55a9] hover:text-white'
      disabledStyles = 'opacity-50 cursor-not-allowed hover:bg-fuschia-60'
      break
    case 'report-next':
      orderStyles = ' bg-[#111D4A] font-bold hover:bg-[#3D55a9] text-white hover:bg-[#3D55a9]'
      disabledStyles = 'opacity-50 cursor-not-allowed hover:bg-fuschia-60'
      break
    case 'custom':
      orderStyles = ''
      disabledStyles = 'opacity-50 cursor-not-allowed'
      break
  }

  return (
    <button
      type="button"
      className={`${getClassNames(commonStyles, sizeStyles, orderStyles, className ?? '')} ${
        disabled ? disabledStyles : ''
      }`}
      {...rest}
      disabled={disabled}
    >
      {loading && (
        <svg
          className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {children}
    </button>
  )
}

export default Button
